import { useCallback, useRef, useState } from 'react';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { gridLocalShortDate, SHORT_DATE_FORMAT } from 'utilities/dateFormats';
import { GridDateProps } from './GridDate.props';
import { HiddenTextField, StyledActionButton, StyledCalendarIcon } from './GridDate.styles';

const GridDate = ({
  cell,
  value,
  readOnly = false,
  disabled = false,
  minDate,
  maxDate,
  icon: Icon,
  onCellsChanged,
  disableFuture = false,
}: GridDateProps) => {
  const [openPicker, setOpenPicker] = useState(false);
  const [localDate, setLocalDate] = useState(value ? new Date(value) : null);
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [currentView, setCurrentView] = useState<'year' | 'month' | 'day'>('year');
  // Used to reset the local date if the user cancels the selection
  const acceptedRef = useRef(false);

  const openToView = localDate ? 'day' : 'year';

  const handleOpenPicker = () => {
    setCurrentView(openToView);
    setOpenPicker(true);
  };

  const handleClosePicker = () => {
    if (!acceptedRef.current) {
      setLocalDate(value ? new Date(value) : null);
    }
    acceptedRef.current = false;
    setCurrentView(openToView);
    setOpenPicker(false);
  };

  const handleChange = (dateParam: Date | null) => {
    if (dateParam) {
      // Update local state to allow proper navigation
      setLocalDate(dateParam);

      // Don't update cell value here - only update on final selection (onAccept).
      // This will prevent intermediate selections from triggering API calls.
    }
  };

  const handleAccept = (dateParam: Date | null) => {
    if (dateParam) {
      acceptedRef.current = true;
      onCellsChanged(gridLocalShortDate(dateParam), cell);
    }
  };

  const handleViewChange = useCallback((view: 'year' | 'month' | 'day') => {
    setCurrentView(view);
  }, []);

  return (
    <>
      <StyledActionButton disabled={disabled} size="small" color="primary" onClick={handleOpenPicker}>
        {Icon ? <Icon size="small" color="secondary" /> : <StyledCalendarIcon fontSize="small" color="secondary" />}
      </StyledActionButton>
      <MobileDatePicker
        open={openPicker}
        onOpen={handleOpenPicker}
        onClose={handleClosePicker}
        value={localDate}
        format={SHORT_DATE_FORMAT}
        onChange={handleChange}
        onAccept={handleAccept}
        openTo={openToView}
        views={['year', 'month', 'day']}
        disabled={!!readOnly}
        minDate={minDate ? new Date(minDate) : undefined}
        maxDate={maxDate ? new Date(maxDate) : undefined}
        disableFuture={disableFuture}
        onViewChange={handleViewChange}
        view={currentView}
        slots={{
          textField: HiddenTextField,
        }}
        slotProps={{
          textField: {
            inputRef: textFieldRef,
            slotProps: {
              input: {
                readOnly: true,
              },
            },
          },
        }}
      />
    </>
  );
};

export default GridDate;
